// removeTable — VQE-Edit edit-action: drop a FROM item by 0-based list
// index via SR-Edit's `remove-from-item` op. Builds a `StructuralEdit` and
// delegates to `commitEdit`. SR-Edit handles comma-stripping AND the leading
// `FROM` keyword removal when the only item is removed; this module only
// validates the index against the structure when one is supplied AND
// surfaces a confirmation hook so the caller can warn before dropping the
// last data source.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove FROM
// item.

import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import { commitEdit, type CommitEditCtx } from '../commitEdit';

export type RemoveTableOptions = {
  /**
   * Optional structure used for index-bounds validation. Lets the caller
   * surface a friendlier error than SR-Edit's "out of range" / "no items".
   * Also drives the `confirmIfOnlySource` hook.
   */
  readonly structure?: QueryStructure;
  /**
   * Confirmation hook invoked when removing the only FROM item. The caller
   * is expected to surface a UI confirm (e.g. browser confirm() or an
   * AlertDialog) and resolve true to proceed, false to cancel. When omitted
   * AND the structure is supplied AND items.length === 1, the edit is
   * aborted silently — caller MUST opt-in via this hook to drop the last
   * source.
   */
  readonly confirmIfOnlySource?: () => boolean | Promise<boolean>;
};

export async function removeTable(
  index: number,
  ctx: CommitEditCtx,
  options: RemoveTableOptions = {},
): Promise<void> {
  if (!Number.isInteger(index) || index < 0) {
    throw new Error(`removeTable: index must be a non-negative integer (got ${index})`);
  }
  if (options.structure) {
    const items = options.structure.fromItems ?? [];
    if (items.length === 0) {
      throw new Error('removeTable: FROM list is empty');
    }
    if (index >= items.length) {
      throw new Error(
        `removeTable: index ${index} out of range (FROM list has ${items.length} item(s))`,
      );
    }
    if (items.length === 1) {
      if (!options.confirmIfOnlySource) return;
      const proceed = await options.confirmIfOnlySource();
      if (!proceed) return;
    }
  }
  await commitEdit({ op: 'remove-from-item', index }, ctx);
}
